/**
 * Maps Ceipal company `date_format` tokens (e.g. `mm/dd/yyyy`) to a formatted date string.
 * Token rules align with Domain Orchestrator `DateService.formatDateForDisplay` (`type: 'js'`).
 */

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** ISO / SQL datetime strings commonly returned from BigQuery. */
const BQ_DATE_VALUE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[ T]\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

function tokenValue(token: string, d: Date): string {
  switch (token.toLowerCase()) {
    case 'mm':
      return String(d.getMonth() + 1).padStart(2, '0');
    case 'dd':
      return String(d.getDate()).padStart(2, '0');
    case 'yyyy':
      return String(d.getFullYear());
    case 'yy':
      return String(d.getFullYear()).slice(-2);
    case 'month':
      return MONTH_NAMES[d.getMonth()] ?? '';
    default:
      return token;
  }
}

/** Format a `Date` using the company pattern (e.g. `mm/dd/yyyy` → `05/28/2026`). */
export function formatDateWithCompanyPattern(date: Date, companyDateFormat: string): string {
  const pattern = companyDateFormat.trim();
  if (!pattern || Number.isNaN(date.getTime())) {
    return '';
  }
  const segments = pattern.split('/').map((s) => s.trim()).filter(Boolean);
  if (!segments.length) {
    return '';
  }
  return segments.map((seg) => tokenValue(seg, date)).join('/');
}

/** Parse BQ / API date strings; returns null when not a recognizable datetime. */
export function parseBqDateValue(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 64) {
    return null;
  }
  if (!BQ_DATE_VALUE_RE.test(trimmed)) {
    return null;
  }
  const normalized = trimmed.includes(' ') && !trimmed.includes('T') ? trimmed.replace(' ', 'T') : trimmed;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function hasTimeComponent(original: string): boolean {
  return /[ T]\d{1,2}:\d{2}/.test(original);
}

function formatTimeFromDate(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const s = d.getSeconds();
  const pad = (n: number) => String(n).padStart(2, '0');
  if (s > 0) {
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
  return `${pad(h)}:${pad(m)}`;
}

export function isLikelyBqDateColumn(columnKey: string | undefined): boolean {
  if (!columnKey?.trim()) {
    return false;
  }
  const k = columnKey.trim().toLowerCase();
  if (k === 'date' || k.endsWith('_date') || k.endsWith('date')) {
    return true;
  }
  if (k.endsWith('_at') || k.includes('datetime') || k.includes('timestamp')) {
    return true;
  }
  return /(^|_)(created|modified|updated|started|ended|expiry|expires|due|submitted|posted|closed|opened)(_|$)/.test(
    k,
  );
}

/**
 * When `companyDateFormat` is set, formats ISO/SQL date cell values for display.
 * Column name hints improve detection; value shape alone is used as a fallback.
 */
export function formatBqDateScalarForDisplay(
  value: unknown,
  companyDateFormat: string | undefined,
  columnKey?: string,
): string | null {
  const pattern = companyDateFormat?.trim();
  if (!pattern || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const columnHint = isLikelyBqDateColumn(columnKey);
  if (!columnHint && !BQ_DATE_VALUE_RE.test(trimmed)) {
    return null;
  }
  const parsed = parseBqDateValue(trimmed);
  if (!parsed) {
    return null;
  }
  const datePart = formatDateWithCompanyPattern(parsed, pattern);
  if (!datePart) {
    return null;
  }
  if (hasTimeComponent(trimmed)) {
    return `${datePart} ${formatTimeFromDate(parsed)}`;
  }
  return datePart;
}
